import { RETURN_ADDRESS_LIMIT, returnAddressFromText } from "@shared/personalise.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, FIELD, PageHeader, PRIMARY_BTN } from "../components/account-ui.tsx";
import { useToast } from "../components/legacy/ui.tsx";
import { type AppConfig, api } from "../lib/api.ts";
import { useAppConfig } from "./__root.tsx";

export const Route = createFileRoute("/app/$customerId/settings")({
  component: SettingsPage,
  beforeLoad: async ({ context, params }) => {
    const config = await context.queryClient.ensureQueryData({
      queryKey: ["config"],
      queryFn: () => api.get<AppConfig>("/config"),
    });
    if (config.user?.role !== "admin") {
      throw redirect({
        to: "/app/$customerId/mycampaigns",
        params: { customerId: params.customerId },
      });
    }
  },
});

function SettingsPage() {
  const { data } = useAppConfig();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const saved = (data?.returnAddress ?? []).join(", ");
  const [text, setText] = useState(saved);

  useEffect(() => {
    setText(saved);
  }, [saved]);

  const save = useMutation({
    mutationFn: () => {
      const parsed = returnAddressFromText(text, []);
      if (!parsed.ok) throw new Error(RETURN_ADDRESS_LIMIT);
      return api.put<{ lines: string[] | null }>("/admin/return-address", { lines: parsed.lines });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["config"] });
      showToast("Return address saved", "success");
    },
    onError: (e: Error) => showToast(e.message, "error"),
  });

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="The return address printed when a campaign has not chosen its own."
      />
      <Card>
        <label className="block text-sm font-medium text-gray-800">
          Return address
          <input
            className={`${FIELD} mt-1`}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <p className="mt-2 text-sm text-gray-600">
          Separate lines with a comma and a space. Leave this empty and save to use the placeholder
          again.
        </p>
        {!data?.returnAddressSet && (
          <p className="mt-2 text-sm text-gray-600">
            Nothing has been saved yet. The placeholder above is what prints until you save a street
            address.
          </p>
        )}
        <button
          type="button"
          className={`${PRIMARY_BTN} mt-4`}
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </button>
      </Card>
    </div>
  );
}
